import type { TFunction } from 'i18next';

import {
  CreateUpdateOrderDto,
  CreateUpdateOrderDtoFromJSON,
  DefaultApi,
  GetLastEditorDto,
  GetOrderDto,
  GetOrderWithCurrencyDto,
} from '../autogenerated/pokApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import { OrderSchema } from '../validation/schemas';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { orderForGrid } from '../../../pok/components/Orders/OrderSearch';
import { getMainCurrencySymbol } from '../../../utils/currencyFormatUtils';
import { TKeys } from '../../hooks/useTranslation';

export interface OrderContextInterface {
  getById: (id: string) => Promise<GetOrderWithCurrencyDto>;
  getAllByPortion: (
    t: TFunction<'orderLng', 'orderLng'>,
    tk: TKeys<'orderLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  getAllByPortionForProject: (
    t: TFunction<'orderLng', 'orderLng'>,
    tk: TKeys<'orderLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    projectId?: string,
    resultProjectId?: string,
    currentProjectId?: string,
  ) => Promise<GridRecord[]>;
  getAllCountForProject: (
    filterText?: string,
    filterColumns?: string,
    projectId?: string,
    resultProjectId?: string,
  ) => Promise<number>;
  getAllByPortionWaiting: (
    t: TFunction<'orderLng', 'orderLng'>,
    tk: TKeys<'orderLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCountWaiting: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllByPortionCompleted: (
    t: TFunction<'orderLng', 'orderLng'>,
    tk: TKeys<'orderLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCountCompleted: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  update: (id: string, dto: CreateUpdateOrderDto) => Promise<void>;
  create: (dto: CreateUpdateOrderDto) => Promise<GetOrderDto>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  reject: (id: string, rejectedNote: string) => Promise<void>;
  accept: (id: string) => Promise<string>;
  deactivate: (id: string) => Promise<void>;
}

export const OrderContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.orderControllerGet(id),
    getAllByPortion: async (
      t: TFunction<'orderLng', 'orderLng'>,
      tk: TKeys<'orderLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.orderControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => orderForGrid(t, tk, item));
    },
    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.orderControllerGetAllCount(filterText || '', filterColumns),
    getAllByPortionForProject: async (
      t: TFunction<'orderLng', 'orderLng'>,
      tk: TKeys<'orderLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      projectId?: string,
      resultProjectId?: string,
      currentProjectId?: string,
    ) => {
      const data = await api.orderControllerGetAllByPortionForProject(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
        projectId || '',
        resultProjectId || '',
      );
      return data.map(item => orderForGrid(t, tk, item, currentProjectId));
    },
    getAllCountForProject: (
      filterText?: string,
      filterColumns?: string,
      projectId?: string,
      resultProjectId?: string,
    ) =>
      api.orderControllerGetAllCountForProject(
        filterText || '',
        filterColumns,
        projectId || '',
        resultProjectId || '',
      ),
    getAllByPortionWaiting: async (
      t: TFunction<'orderLng', 'orderLng'>,
      tk: TKeys<'orderLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.orderControllerGetAllByPortionWaiting(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => orderForGrid(t, tk, item));
    },
    getAllCountWaiting: (filterText?: string, filterColumns?: string) =>
      api.orderControllerGetAllCountWaiting(filterText || '', filterColumns),
    getAllByPortionCompleted: async (
      t: TFunction<'orderLng', 'orderLng'>,
      tk: TKeys<'orderLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.orderControllerGetAllByPortionCompleted(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => orderForGrid(t, tk, item));
    },
    getAllCountCompleted: (filterText?: string, filterColumns?: string) =>
      api.orderControllerGetAllCountCompleted(filterText || '', filterColumns),
    create: (dto: CreateUpdateOrderDto) => api.orderControllerCreate(dto),
    update: (id: string, dto: CreateUpdateOrderDto) =>
      api.orderControllerUpdate(id, dto),
    getLastEditor: (id: string) => api.orderControllerGetLastEditor(id),
    reject: (id: string, rejectedNote: string) =>
      api.orderControllerReject(id, { rejectedNote }),
    accept: (id: string) => api.orderControllerAccept(id),
    deactivate: (id: string) => api.orderControllerDeactivate(id),
  };
};

export const newOrder = (
  projectId?: string,
  startDate?: Date,
  endDate?: Date,
) => {
  return CreateUpdateOrderDtoFromJSON({
    projectId,
    addTeamToEmails: false,
    orderEstimateItemsIds: [],
    orderEmployeesIds: [],
    emails: [],
    currency: getMainCurrencySymbol(),
    startDate: startDate ?? null,
    endDate: endDate ?? null,
  });
};

export const validate = (project: CreateUpdateOrderDto) => {
  return validateAgainst(OrderSchema, project);
};

export const convert = (
  order?: GetOrderDto,
  projectId?: string,
  projectStartDate?: Date,
  projectEndDate?: Date,
) => {
  if (!order) {
    return newOrder(projectId, projectStartDate, projectEndDate);
  }

  const converted = CreateUpdateOrderDtoFromJSON(order);
  converted.orderEmployeesIds =
    order.orderEmployees
      ?.filter(({ active }) => active)
      .map(({ employee }) => employee?.id) || [];
  converted.orderEstimateItemsIds =
    order.orderEstimateItems
      ?.filter(({ active }) => active)
      .map(({ estimateItem }) => estimateItem.id) || [];
  converted.projectId = order.project.id;
  converted.teamId = order.team.id;
  converted.pathPhaseDictionaryId = order.pathPhaseDictionary?.id;

  return converted;
};
